import { Injectable } from '@angular/core';
import {
  AnalyticsService,
  Claim,
  CookieTokenService,
  FeatureFlagService,
} from '@domgen/dgx-components';
import { CookieService } from 'ngx-cookie-service';

interface ClaimEvent {
  pagename?: string;
  eventClaims?: string;
  claim: Claim | null;
  checkClaimStarted?: boolean;
  questionNumber?: number;
  fireDistinct?: boolean;
  claimGroup?: string;
  repairer?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SmartFixAnalyticsService {
  constructor(
    private readonly analyticsService: AnalyticsService,
    private cookieToken: CookieTokenService,
    private cookieService: CookieService,
    private featureFlagService: FeatureFlagService
  ) {}

  triggerSmartFixEvent(
    eventLabel: string,
    eventClaims: string,
    claim: Claim,
    eventAction: string,
    eventCategory?: string,
    pagename?: string
  ): void {
    const { isBoiler, isBoilerGenAIFlagEnabled } =
      this.isBoilerGenAIFlow(claim);

    const gaTrack = {
      ...(isBoiler &&
        isBoilerGenAIFlagEnabled && {
          category: 'claims-2.0',
        }),
      clientName: 'DG Claims',
      eventClaims: eventClaims?.replace(/-dgx$/, '') as string,
      eventCategory: eventCategory || 'claimsRequest-dgx',
      DigitalID: claim?.reflect?.user || 'DigitalID not found',
      customerID: this.cookieToken.getCustomerID(),
      planNumber: claim?.reflect?.planNumber,
      productName: `${claim?.reflect?.manufacturer} ${claim?.reflect?.productType}`,
      UnderManufacturerGuarantee: claim?.getData?.PlanInWarrantyPeriod
        ? 'Yes'
        : 'No',
      claimGroup: 'dgx',
      claimedModelNumber: claim?.getData?.ModelNumber,
      claimedSerialNumber: claim?.getData?.SerialNumber,
      claimedAccountBrand: claim?.getData?.BookingOEM,
      claimedManufacturer: claim?.reflect?.manufacturer,
      claimedClientName: claim?.claimedClientName,
      excessClaimsMessage: claim?.excess?.type
        ? `${claim?.excess?.type === 'CALLOUT' ? 'CALLOUT' : 'EXCESS'}`
        : 'NO',
      companyCode: claim?.reflect?.companyCode,
      countryCode: claim?.reflect?.countryCode,
      ...(claim?.isGasAppliance && { boilerType: claim?.reflect?.productType }),
    };

    this.analyticsService.trigger('genericGAEvent', {
      ...gaTrack,
      ...{ pagename },
      eventLabel,
      eventAction,
    });
  }

  pageViewEvent(pagename: string, claim: Claim) {
    const { isBoiler, isBoilerGenAIFlagEnabled } =
      this.isBoilerGenAIFlow(claim);

    this.analyticsService.trigger('spa-pageview', {
      ...(isBoiler &&
        isBoilerGenAIFlagEnabled && {
          category: 'claims-2.0',
        }),
      clientName: 'DG Claims',
      pagename: pagename,
      customerID: this.cookieToken.getCustomerID(),
      planNumber: claim?.reflect?.planNumber,
      claimGroup: 'dgx',
      claimedModelNumber: claim?.getData?.ModelNumber,
      claimedSerialNumber: claim?.getData?.SerialNumber,
      claimedAccountBrand: claim?.getData?.BookingOEM,
      claimedManufacturer: claim?.reflect?.manufacturer,
      claimedClientName: claim?.claimedClientName,
      productName: `${claim?.reflect?.manufacturer} ${claim?.reflect?.productType}`,
      excessClaimsMessage: claim?.excess?.type ? 'YES' : 'NO',
      companyCode: claim?.reflect?.companyCode,
      countryCode: claim?.reflect?.countryCode,
    });
  }

  clearSmartFixRoutingCookie() {
    const cookieDomain = window.location.host.endsWith('domesticandgeneral.com')
      ? '.domesticandgeneral.com'
      : '.domgentest.cloud';
    this.cookieService.set('sfx_routing', '', -1, '/', cookieDomain); //expire our custom sfx cookie after we are done with it
  }

  private isBoilerGenAIFlow(claim: Claim): {
    isBoiler: boolean;
    isBoilerGenAIFlagEnabled: boolean;
  } {
    const isBoiler =
      claim?.reflect?.productType?.toLowerCase().includes('boiler') || false;
    const isBoilerGenAIFlagEnabled =
      this.featureFlagService.isFeatureFlagEnabled(
        'smart-fix-genai-chatbot-boiler'
      );
    return { isBoiler, isBoilerGenAIFlagEnabled };
  }
}
